import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import React, { useEffect, useState } from 'react';
import { useMaestroContext } from '../context/MaestroContext';
import maestroService from '../maestroService';
import { EtapaSetup } from '../models/ciclo';
import { dispararAcaoSimulada } from '../utils/acaoSimulada';
import { formatarData } from '../utils/formatters';
import { LABEL_AREA, LABEL_TIPO_FERIADO } from '../utils/labels';
import ModalGerenciarCalendario from './ModalGerenciarCalendario';
import SeletorEscopo from './SeletorEscopo';

const ETAPAS = [
  'Escopo', 'Competência', 'Empresas', 'Prazo oficial', 'Calendário de dias úteis',
  'Áreas', 'Ações de PDCA importadas', 'Revisão', 'Confirmação',
];

// Situações recorrentes de fechamento — atalho para não digitar do zero; o texto continua
// editável depois de escolhido, então isso é ponto de partida, não uma opção fixa.
const PDCA_PRESETS: Array<{ titulo: string; problema: string; acaoCorretiva: string }> = [
  { titulo: 'Atraso na conciliação bancária', problema: 'Conciliação bancária não concluída dentro do prazo do ciclo.', acaoCorretiva: 'Priorizar a conciliação pendente e comunicar o responsável sobre o novo prazo.' },
  { titulo: 'Evidência não anexada', problema: 'Atividade concluída sem a evidência obrigatória anexada.', acaoCorretiva: 'Cobrar o anexo da evidência e revisar o checklist da atividade antes do próximo fechamento.' },
  { titulo: 'Divergência contábil x fiscal', problema: 'Divergência identificada entre o saldo contábil e o relatório fiscal.', acaoCorretiva: 'Investigar a origem da divergência e realizar o ajuste contábil necessário.' },
  { titulo: 'Aprovação SOX atrasada', problema: 'Aprovação SOX pendente há mais tempo do que o SLA definido.', acaoCorretiva: 'Escalar para o aprovador responsável e registrar a justificativa do atraso.' },
  { titulo: 'Dependência não cumprida', problema: 'Atividade predecessora não concluída no prazo, bloqueando a sucessora.', acaoCorretiva: 'Renegociar o prazo com a área responsável pela predecessora e ajustar o cronograma.' },
];

// Sempre a competência corrente por padrão — o usuário troca se estiver abrindo um ciclo
// atrasado/adiantado, mas o caso comum (mês vigente) não deveria exigir digitação manual.
const competenciaAtual = (): string => {
  const agora = new Date();
  return `${String(agora.getMonth() + 1).padStart(2, '0')}/${agora.getFullYear()}`;
};

const WizardAberturaCiclo: React.FC<{ aberto: boolean; onFechar: () => void }> = ({ aberto, onFechar }) => {
  const { cicloAtivo, empresasEscopo, areas, acoesPDCA, feriados, dependencias, recarregar } = useMaestroContext();
  const [passo, setPasso] = useState(0);
  const [competencia, setCompetencia] = useState(competenciaAtual);
  const [prazoOficial, setPrazoOficial] = useState('');
  const [enviando, setEnviando] = useState(false);

  const [etapasSetup, setEtapasSetup] = useState<EtapaSetup[]>([]);
  const [percentualSetup, setPercentualSetup] = useState(0);

  // MAESTRO-RN-24 — sem esses três IDs o ciclo abre "vazio" (nenhuma atividade instanciada do
  // template). Já vimos isso acontecer de verdade: nada aqui é escolhido manualmente na maioria
  // dos casos — auto-seleciona a versão publicada mais recente de cada um, e só bloqueia a
  // abertura se não existir template publicado (sem template não há o que instanciar).
  const [templates, setTemplates] = useState<any[]>([]);
  const [scoreModels, setScoreModels] = useState<any[]>([]);
  const [calendarios, setCalendarios] = useState<any[]>([]);

  const [gerenciandoCalendario, setGerenciandoCalendario] = useState(false);

  const [criandoPdca, setCriandoPdca] = useState(false);
  const [pdcaProblema, setPdcaProblema] = useState('');
  const [pdcaAcaoCorretiva, setPdcaAcaoCorretiva] = useState('');
  const [pdcaEmpresaId, setPdcaEmpresaId] = useState(0);
  const [pdcaPrazo, setPdcaPrazo] = useState('');
  const [enviandoPdca, setEnviandoPdca] = useState(false);

  useEffect(() => {
    if (!aberto) return;
    setPasso(0);
    setCompetencia(competenciaAtual());
    setPrazoOficial('');
    setCriandoPdca(false);
    maestroService.obterSetup()
      .then(setup => { setEtapasSetup(setup.etapas); setPercentualSetup(setup.percentualConcluido); })
      .catch(() => { setEtapasSetup([]); setPercentualSetup(0); });
    maestroService.listarTemplateVersions().then(setTemplates).catch(() => setTemplates([]));
    maestroService.listarScoreModelVersions().then(setScoreModels).catch(() => setScoreModels([]));
    maestroService.listarCalendarios().then(setCalendarios).catch(() => setCalendarios([]));
  }, [aberto]);

  const templatePublicado = templates.filter(t => t.status === 'PUBLICADO').sort((a, b) => b.id - a.id)[0];
  const calendarioPublicado = calendarios.filter(c => c.status === 'PUBLICADO').sort((a, b) => b.versao - a.versao)[0];
  const scoreModelSelecionado = [...scoreModels].sort((a, b) => (b.status === 'PUBLICADO' ? 1 : 0) - (a.status === 'PUBLICADO' ? 1 : 0) || b.id - a.id)[0];

  const setupCompleto = percentualSetup >= 100;
  const etapasPendentes = etapasSetup.filter(e => !e.concluida);

  const acoesPdcaImportaveis = acoesPDCA.filter(a => a.status === 'ABERTA' || a.status === 'EM_ANDAMENTO');

  const criarAcaoPdca = () => {
    if (!pdcaProblema.trim() || !pdcaAcaoCorretiva.trim() || !pdcaEmpresaId) return;
    setEnviandoPdca(true);
    maestroService.criarPdca({
      cicloId: cicloAtivo.id,
      empresaId: pdcaEmpresaId,
      problema: pdcaProblema.trim(),
      acaoCorretiva: pdcaAcaoCorretiva.trim(),
      prazoAcao: pdcaPrazo || undefined,
    })
      .then(() => { setCriandoPdca(false); setPdcaProblema(''); setPdcaAcaoCorretiva(''); setPdcaEmpresaId(0); setPdcaPrazo(''); recarregar(); })
      .catch((e: any) => dispararAcaoSimulada(e?.response?.data?.message ?? 'Não foi possível criar a ação de PDCA.'))
      .finally(() => setEnviandoPdca(false));
  };

  const confirmarAbertura = () => {
    if (!competencia.trim() || !prazoOficial) {
      dispararAcaoSimulada('Informe a competência e o prazo oficial antes de abrir o ciclo.');
      return;
    }
    if (!templatePublicado) {
      dispararAcaoSimulada('Nenhum template publicado — publique um template na tela de Setup antes de abrir o ciclo.');
      return;
    }
    setEnviando(true);
    maestroService.abrirCiclo(
      competencia.trim(),
      empresasEscopo.map(e => e.id),
      prazoOficial,
      undefined,
      templatePublicado.id,
      scoreModelSelecionado?.id,
      calendarioPublicado?.id,
    )
      .then(() => { recarregar(); onFechar(); })
      .catch((e: any) => dispararAcaoSimulada(e?.response?.data?.message ?? 'Não foi possível abrir o ciclo.'))
      .finally(() => setEnviando(false));
  };

  const conteudoPasso = () => {
    switch (ETAPAS[passo]) {
      case 'Escopo':
        return <><p>Selecione o escopo do novo ciclo de fechamento.</p><SeletorEscopo /></>;

      case 'Competência':
        return (
          <>
            <label className='mc-campo'>Competência (MM/AAAA)
              <input type='text' placeholder='Ex.: 08/2026' value={competencia} onChange={e => setCompetencia(e.target.value)} />
            </label>
            <p className='mc-texto-neutro' style={{ marginTop: 8 }}>Preenchida automaticamente com o mês atual — troque se estiver abrindo um ciclo de competência diferente.</p>
          </>
        );

      case 'Empresas':
        return empresasEscopo.length === 0
          ? <p className='mc-texto-neutro'>Nenhuma empresa selecionada — volte à etapa de Escopo e selecione ao menos uma.</p>
          : (
            <ul className='mc-wizard-checklist'>
              {empresasEscopo.map(e => (
                <li key={e.id} className='is-ok'>
                  <i className='bi bi-building' />
                  <div>
                    <strong>{e.nome}</strong>
                    <small>{e.cnpj || 'CNPJ não cadastrado'}{e.regimeTributario ? ` · Regime: ${e.regimeTributario}` : ' · Regime tributário não informado'}</small>
                  </div>
                </li>
              ))}
            </ul>
          );

      case 'Prazo oficial':
        return (
          <label className='mc-campo'>Data de fechamento oficial
            <input type='date' value={prazoOficial} onChange={e => setPrazoOficial(e.target.value)} />
          </label>
        );

      case 'Calendário de dias úteis':
        return (
          <>
            {feriados.length === 0 ? (
              <p className='mc-texto-neutro' style={{ marginTop: 0 }}>Nenhum feriado cadastrado ainda. Até que o calendário seja configurado, todos os dias contam como úteis para os prazos do ciclo.</p>
            ) : (
              <ul className='mc-wizard-checklist'>
                {feriados.slice(0, 6).map(f => (
                  <li key={f.id} className='is-ok'>
                    <i className='bi bi-calendar-event' />
                    <div><strong>{f.nome}</strong><small>{formatarData(f.data)} · {LABEL_TIPO_FERIADO[f.tipo]}</small></div>
                  </li>
                ))}
                {feriados.length > 6 && (
                  <li><i className='bi bi-three-dots' /><div><small>+{feriados.length - 6} feriado(s) adicionais já cadastrados no calendário</small></div></li>
                )}
              </ul>
            )}
            <button type='button' className='mc-btn mc-btn--secondary' onClick={() => setGerenciandoCalendario(true)}>
              <i className='bi bi-calendar-plus' /> Gerenciar calendário
            </button>
          </>
        );

      case 'Áreas':
        return areas.length === 0
          ? <p className='mc-texto-neutro'>Nenhuma área configurada ainda — volte ao Setup para cadastrar as áreas responsáveis pelo fechamento.</p>
          : (
            <>
              <p className='mc-texto-neutro' style={{ marginTop: 0 }}>Áreas responsáveis por atividades que vão compor o novo ciclo:</p>
              <ul className='mc-wizard-checklist'>
                {areas.map(a => (
                  <li key={a.id} className='is-ok'>
                    <i className='bi bi-diagram-3' style={a.cor ? { color: a.cor } : undefined} />
                    <div>
                      <strong>{a.nome || LABEL_AREA[a.tipo]}</strong>
                      {a.nome && a.nome !== LABEL_AREA[a.tipo] && <small>{LABEL_AREA[a.tipo]}</small>}
                    </div>
                  </li>
                ))}
              </ul>
            </>
          );

      case 'Ações de PDCA importadas':
        return (
          <>
            {acoesPdcaImportaveis.length === 0 ? (
              <p className='mc-texto-neutro' style={{ marginTop: 0 }}>Nenhuma ação de PDCA em aberto no ciclo atual para importar automaticamente.</p>
            ) : (
              <ul className='mc-wizard-checklist'>
                {acoesPdcaImportaveis.map(a => (
                  <li key={a.id} className='is-ok'>
                    <i className='bi bi-arrow-repeat' />
                    <div><strong>{a.problema}</strong><small>Etapa {a.etapa} · Prazo {a.prazo ? formatarData(a.prazo) : '—'}</small></div>
                  </li>
                ))}
              </ul>
            )}

            {!cicloAtivo.id ? (
              <p className='mc-texto-neutro'>Como este é o primeiro ciclo do módulo, ainda não existe um ciclo em andamento para vincular uma nova ação de PDCA — essa opção passa a ficar disponível a partir do segundo ciclo.</p>
            ) : !criandoPdca ? (
              <button type='button' className='mc-btn mc-btn--secondary' onClick={() => { setPdcaEmpresaId(empresasEscopo[0]?.id ?? 0); setCriandoPdca(true); }}>
                <i className='bi bi-plus-lg' /> Nova ação de PDCA
              </button>
            ) : (
              <div className='mc-pdca-form'>
                <div className='mc-pdca-form__presets'>
                  {PDCA_PRESETS.map((preset, i) => (
                    <button
                      key={i}
                      type='button'
                      className='mc-btn mc-btn--secondary mc-btn--sm'
                      onClick={() => { setPdcaProblema(preset.problema); setPdcaAcaoCorretiva(preset.acaoCorretiva); }}
                    >
                      {preset.titulo}
                    </button>
                  ))}
                </div>

                <label className='mc-campo'>Empresa
                  <select value={pdcaEmpresaId} onChange={e => setPdcaEmpresaId(Number(e.target.value))}>
                    <option value={0}>Selecione...</option>
                    {empresasEscopo.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
                  </select>
                </label>
                <label className='mc-campo'>Problema
                  <textarea rows={2} placeholder='O que aconteceu' value={pdcaProblema} onChange={e => setPdcaProblema(e.target.value)} />
                </label>
                <label className='mc-campo'>Ação corretiva
                  <textarea rows={2} placeholder='O que vai ser feito' value={pdcaAcaoCorretiva} onChange={e => setPdcaAcaoCorretiva(e.target.value)} />
                </label>
                <label className='mc-campo'>Prazo
                  <input type='date' value={pdcaPrazo} onChange={e => setPdcaPrazo(e.target.value)} />
                </label>
                <div className='mc-pdca-form__acoes'>
                  <button type='button' className='mc-btn mc-btn--primary' disabled={enviandoPdca || !pdcaProblema.trim() || !pdcaAcaoCorretiva.trim() || !pdcaEmpresaId} onClick={criarAcaoPdca}>
                    {enviandoPdca ? 'Criando...' : 'Criar ação'}
                  </button>
                  <button type='button' className='mc-btn mc-btn--secondary' onClick={() => setCriandoPdca(false)}>Cancelar</button>
                </div>
              </div>
            )}
          </>
        );

      case 'Revisão':
        return (
          <ul className='mc-wizard-checklist'>
            <li className={templatePublicado ? 'is-ok' : 'is-pendente'}>
              <i className={templatePublicado ? 'bi bi-file-earmark-check' : 'bi bi-x-circle-fill'} />
              <div>
                <strong>{templatePublicado ? `Template "${templatePublicado.nome}"` : 'Nenhum template publicado'}</strong>
                <small>{templatePublicado ? 'As atividades do novo ciclo serão geradas a partir dele' : 'Publique um template na tela de Setup antes de abrir o ciclo'}</small>
              </div>
            </li>
            <li className='is-ok'>
              <i className='bi bi-calendar-event' />
              <div><strong>{calendarioPublicado ? `Calendário "${calendarioPublicado.nome}"` : 'Sem calendário publicado'}</strong><small>{calendarioPublicado ? 'Feriados considerados no cálculo de prazos' : 'Todos os dias contarão como úteis'}</small></div>
            </li>
            <li className='is-ok'>
              <i className='bi bi-trophy' />
              <div><strong>{scoreModelSelecionado ? `Modelo de pontuação "${scoreModelSelecionado.nome}"` : 'Nenhum modelo de pontuação'}</strong><small>{scoreModelSelecionado ? `Status: ${scoreModelSelecionado.status}` : 'Ranking do ciclo não terá pontuação'}</small></div>
            </li>
            <li className='is-ok'>
              <i className='bi bi-building' />
              <div><strong>{empresasEscopo.length} empresa(s)</strong><small>{empresasEscopo.map(e => e.nome).join(', ') || '—'}</small></div>
            </li>
            <li className='is-ok'>
              <i className='bi bi-calendar3' />
              <div><strong>Competência {competencia || '—'}</strong><small>Mês de referência do fechamento</small></div>
            </li>
            <li className='is-ok'>
              <i className='bi bi-flag' />
              <div><strong>Prazo oficial {prazoOficial ? formatarData(prazoOficial) : '—'}</strong><small>Data limite de encerramento</small></div>
            </li>
            <li className='is-ok'>
              <i className='bi bi-arrow-repeat' />
              <div><strong>{acoesPdcaImportaveis.length} ação(ões) de PDCA</strong><small>Serão importadas automaticamente ao abrir</small></div>
            </li>
            <li className='is-ok'>
              <i className='bi bi-link-45deg' />
              <div><strong>{dependencias.length} dependência(s) no ciclo atual</strong><small>Referência — recriadas manualmente nas atividades do novo ciclo</small></div>
            </li>
          </ul>
        );

      case 'Confirmação':
        return (
          <div className='mc-estado'>
            <i className='bi bi-check2-circle' />
            <strong>Tudo pronto para abrir o ciclo</strong>
            <p>Se precisar corrigir algo, use "Voltar". Ao clicar em "Abrir ciclo", o novo ciclo de fechamento é criado e as atividades do template selecionado são geradas automaticamente para as empresas do escopo escolhido.</p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={aberto} onClose={onFechar} maxWidth='sm' fullWidth PaperProps={{ className: 'mc-dialog' }}>
      <DialogTitle>Abertura de Ciclo — {ETAPAS[passo]}</DialogTitle>
      <DialogContent>
        {!setupCompleto && (
          <div className='mc-wizard-bloqueio'>
            <strong><i className='bi bi-exclamation-triangle' /> O Setup do Maestro está {percentualSetup}% concluído</strong>
            <ul>{etapasPendentes.map(e => <li key={e.id}>{e.titulo}: {e.pendencias[0]?.descricao ?? `${e.itensPendentes} item(ns) pendente(s)`}</li>)}</ul>
          </div>
        )}

        <div className='mc-wizard-passos'>
          {ETAPAS.map((etapa, i) => (
            <div key={etapa} className={`mc-wizard-passos__item ${i === passo ? 'is-active' : i < passo ? 'is-concluido' : ''}`}>
              <span className='mc-wizard-passos__barra' />
              <span className='mc-wizard-passos__numero'>{i + 1}</span>
            </div>
          ))}
        </div>

        <div className='mc-wizard-conteudo'>{conteudoPasso()}</div>

        <div className='mc-wizard-acoes'>
          <button type='button' className='mc-btn mc-btn--secondary' onClick={onFechar}>Cancelar</button>
          <div>
            {passo > 0 && <button type='button' className='mc-btn mc-btn--secondary' onClick={() => setPasso(p => p - 1)}>Voltar</button>}
            {passo < ETAPAS.length - 1
              ? <button type='button' className='mc-btn mc-btn--primary' onClick={() => setPasso(p => p + 1)}>Avançar</button>
              : (
                <button
                  type='button'
                  className='mc-btn mc-btn--primary'
                  disabled={!setupCompleto || !templatePublicado || enviando}
                  title={!setupCompleto ? 'Resolva as pendências obrigatórias do Setup antes de abrir o ciclo.' : !templatePublicado ? 'Publique um template na tela de Setup antes de abrir o ciclo.' : undefined}
                  onClick={confirmarAbertura}
                >
                  {enviando ? 'Abrindo...' : 'Abrir ciclo'}
                </button>
              )}
          </div>
        </div>
      </DialogContent>
      <ModalGerenciarCalendario aberto={gerenciandoCalendario} onFechar={() => setGerenciandoCalendario(false)} />
    </Dialog>
  );
};

export default WizardAberturaCiclo;
